import React from 'react';
import { Box, Button, Typography, Paper } from '@mui/material';
import { 
  BlockRounded as BlockIcon,
  HomeRounded as HomeIcon
} from '@mui/icons-material';
import { styled } from '@mui/material/styles';
import { keyframes } from '@emotion/react';

const fadeInUp = keyframes`
  from {
    opacity: 0;
    transform: translateY(30px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const float = keyframes`
  0%, 100% {
    transform: translateY(0px);
  }
  50% {
    transform: translateY(-10px);
  }
`;

const UnauthorizedContainer = styled(Box)(() => ({
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'center',
  alignItems: 'center',
  height: '100vh',
  width: '100vw',
  top: 0,
  left: 0,
  overflow: 'hidden',
  background: 'radial-gradient(circle at center, #c4262e 0%, #a21e26 50%, #7a161c 100%)',
  position: 'fixed',
  '&::before': {
    content: '""',
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: `
      radial-gradient(circle at 20% 30%, rgba(196, 38, 46, 0.15) 0%, transparent 50%), 
      radial-gradient(circle at 80% 70%, rgba(45, 54, 58, 0.1) 0%, transparent 50%)
    `,
  }
}));

const UnauthorizedPaper = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(5),
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  gap: theme.spacing(3),
  minWidth: '90vw',
  maxWidth: '550px',
  textAlign: 'center',
  borderRadius: theme.spacing(3),
  boxShadow: '0 25px 50px rgba(0,0,0,0.3)',
  animation: `${fadeInUp} 0.6s ease-out`,
  position: 'relative',
  zIndex: 1,
  [theme.breakpoints.up('sm')]: {
    minWidth: 450,
  }
}));

const IconWrapper = styled(Box)(() => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: 100,
  height: 100,
  borderRadius: '50%',
  background: 'linear-gradient(135deg, #ff9800 0%, #f57c00 100%)',
  boxShadow: '0 15px 35px rgba(255, 152, 0, 0.3)',
  animation: `${float} 3s ease-in-out infinite`,
}));

interface UnAuthorizedScreenProps {
  userName?: string;
  userEmail?: string;
}

const UnAuthorizedScreen: React.FC<UnAuthorizedScreenProps> = ({ 
  userName = 'N/A',
  userEmail = 'N/A'
}) => {

  const handleGoHome = () => {
    window.location.href = 'https://www.edwardsvacuum.com/';
  };

  return (
    <UnauthorizedContainer>
      <UnauthorizedPaper elevation={6}>
        <IconWrapper>
          <BlockIcon sx={{ fontSize: 50, color: 'white' }} />
        </IconWrapper>

        <Typography 
          variant="h4" 
          component="h1" 
          sx={{ 
            fontWeight: 700, 
            color: '#2d363a',
            mb: 1 
          }}
        >
          Access Restricted
        </Typography>

        <Typography 
          variant="h6" 
          sx={{ 
            color: '#666',
            fontWeight: 400,
            mb: 1
          }}
        >
          You are not authorized to access this application
        </Typography>

        <Box 
          sx={{ 
            bgcolor: '#f5f5f5', 
            p: 2, 
            borderRadius: 2, 
            width: '100%',
            border: '1px solid #e0e0e0'
          }}
        >
          <Typography variant="body2" sx={{ color: '#555', mb: 0.5 }}>
            <strong>User:</strong> {userName}
          </Typography>
          <Typography variant="body2" sx={{ color: '#555' }}>
            <strong>Email:</strong> {userEmail}
          </Typography>
        </Box>

        <Typography 
          variant="body1" 
          sx={{ 
            color: '#5a5a5a',
            lineHeight: 1.6,
            px: 2
          }}
        >
          The Business Case Tracker (BCT) is restricted to authorized Edwards Vacuum personnel only. 
          If you believe you should have access, please contact your system administrator.
        </Typography>

        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, width: '100%', mt: 2 }}>
          <Button
            variant="outlined"
            size="medium"
            startIcon={<HomeIcon />}
            onClick={handleGoHome}
            sx={{ 
              color: '#2d363a',
              borderColor: '#d0d0d0',
              '&:hover': {
                borderColor: '#c4262e',
                bgcolor: 'rgba(196, 38, 46, 0.05)',
              }
            }}
          >
            Go to Edwards Vacuum Home
          </Button>
        </Box>

        <Box sx={{ mt: 3, pt: 3, borderTop: '1px solid #e0e0e0', width: '100%' }}>
          <Typography variant="caption" sx={{ color: '#888', display: 'block', mb: 0.5 }}>
            BCT - Business Case Tracker
          </Typography>
          <Typography variant="caption" sx={{ color: '#aaa' }}>
            Edwards Vacuum © {new Date().getFullYear()}
          </Typography>
        </Box>
      </UnauthorizedPaper>
    </UnauthorizedContainer>
  );
};

export default UnAuthorizedScreen;